import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type CSSProperties,
    type MouseEvent,
    type ReactNode,
    type Ref,
} from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import FJUtil from '../../../util/FJUtil.js';
import FJAnchor from './FJAnchor.tsx';
import styles from './FJDropDown.module.less';
import FJDropDownManager from '../../js/FJDropDownManager.js';
import FJMathUtil from '../../../util/FJMathUtil.js';

type FJDropDownPlacement =
    | 'bottomLeft'
    | 'bottomCenter'
    | 'bottomRight'
    | 'topLeft'
    | 'topCenter'
    | 'topRight'
    | 'rightTop'
    | 'rightCenter'
    | 'rightBottom'
    | 'leftTop'
    | 'leftCenter'
    | 'leftBottom';

interface FJDropDownMenuPadding {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

interface FJDropDownRenderTriggerArgs {
    ref: Ref<HTMLElement>;
    active: boolean | string;
    style: CSSProperties;
    onClick: (e: MouseEvent<HTMLElement>) => void;
    onMouseMove: (e: MouseEvent<HTMLElement>) => void;
}

interface FJDropDownRenderOverlayArgs {
    ref: Ref<HTMLElement>;
    active: boolean | string;
    hide: (e?: unknown) => void;
    show: (e?: unknown) => void;
}

interface FJDropDownProps {
    renderTrigger: (args: FJDropDownRenderTriggerArgs) => ReactNode;
    renderOverlay: (args: FJDropDownRenderOverlayArgs) => ReactNode;
    level?: number;
    placement?: FJDropDownPlacement | string;
    ignoreManager?: boolean;
    trigger?: 'click' | 'hover';
    isNeedBindWheelEvent?: boolean;
    couldEventHide?: boolean;
    disabled?: boolean;
    triggerClick?: (e: MouseEvent<HTMLElement>) => void;
    offsetX?: number;
    offsetY?: number;
    menuPadding?: FJDropDownMenuPadding;
    isCanScrollPage?: boolean;
    fitInViewAnyway?: boolean;
    isAdaptive?: boolean;
    offsetLeft?: number;
    offsetTop?: number;
    noAnimation?: boolean;
    onHide?: (e?: unknown) => void;
    onShow?: () => void;
    overlayClassName?: string;
    overlayStyle?: CSSProperties;
    isTopLayer?: boolean;
    canTriggerActive?: boolean;
    hasArrow?: boolean;
    active?: boolean;
    triggerDisplay?: string;
    zIndex?: number;
    arrowClass?: string;
    useAnchorBody?: boolean;
    arrowStyle?: CSSProperties;
}

const OVERLAY_CLOSE_MS = 240;

const _judgeBrowser = () => {
    const userAgent = navigator.userAgent;
    const isFF = userAgent.indexOf('Firefox') > -1;
    const isChrome = userAgent.indexOf('Chrome') > -1 && userAgent.indexOf('Safari') > -1;
    return { isFF, isChrome };
};

export default function FJDropDown(props: FJDropDownProps) {
    const {
        renderTrigger,
        renderOverlay,
        overlayClassName,
        overlayStyle = {},
        isTopLayer,
        canTriggerActive,
        hasArrow = false,
        active = true,
        triggerDisplay,
        zIndex,
        arrowClass,
        useAnchorBody,
        arrowStyle,
    } = props;

    const [shown, setShown] = useState(false);
    const [left, setLeft] = useState(0);
    const [top, setTop] = useState(0);

    const triggerEle = useRef<HTMLElement | null>(null);
    const overlayEle = useRef<HTMLDivElement | null>(null);
    const menuRef = useRef<HTMLElement | null>(null);

    const isBindEventRef = useRef(false);
    const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const resizeDebounceRef = useRef<ReturnType<typeof _.debounce> | null>(null);
    const placementRef = useRef(props.placement || 'bottomCenter');
    const levelRef = useRef(props.level ?? 1);
    const idRef = useRef<number | undefined>(undefined);
    const shownRef = useRef(false);
    const propsRef = useRef(props);
    const hideEventRef = useRef<unknown>(undefined);
    const mountedTransitionRef = useRef(false);

    propsRef.current = props;
    levelRef.current = props.level ?? 1;

    if (idRef.current === undefined && !props.ignoreManager) {
        idRef.current = FJDropDownManager.createId();
    }

    useEffect(() => {
        shownRef.current = shown;
    }, [shown]);

    const getTriggerEle = useCallback(() => triggerEle.current, []);
    const getOverlayEle = useCallback(() => overlayEle.current, []);

    const getConnectRect = useCallback((): { x: number; y: number; width: number; height: number } | undefined => {
        const overlayEl = getOverlayEle();
        if (!overlayEl) return undefined;
        const { offsetX = 0, offsetY = 0 } = propsRef.current;
        let { width, height } = overlayEl.getBoundingClientRect();
        let rect: { x: number; y: number; width: number; height: number } | undefined;
        const p = placementRef.current;

        switch (p) {
            case 'bottomLeft':
            case 'bottomCenter':
            case 'bottomRight': {
                if (offsetY > 0) {
                    rect = { x: left, y: top - offsetY, width, height: offsetY };
                }
                break;
            }
            case 'topLeft':
            case 'topCenter':
            case 'topRight': {
                if (offsetY > 0) {
                    rect = { x: left, y: top + height, width, height: offsetY };
                }
                break;
            }
            case 'rightTop':
            case 'rightCenter':
            case 'rightBottom': {
                if (offsetX > 0) {
                    rect = { x: left - offsetX, y: top, width: offsetX, height };
                }
                break;
            }
            case 'leftTop':
            case 'leftCenter':
            case 'leftBottom': {
                if (offsetX > 0) {
                    rect = { x: left + width, y: top, width: offsetX, height };
                }
                break;
            }
            default:
                break;
        }
        return rect;
    }, [left, top, getOverlayEle]);

    const checkCanBeHide = useCallback(
        (e: globalThis.MouseEvent) => {
            const { couldEventHide = true } = propsRef.current;
            const isInTriggerArea = FJUtil.isPanelArea(e, getTriggerEle());
            const isInOverlayArea = FJUtil.isPanelArea(e, getOverlayEle());
            let isInConnectRect = false;
            const connectRect = getConnectRect();
            if (connectRect) {
                const point = { x: e.clientX, y: e.clientY };
                isInConnectRect = FJMathUtil.isPointInRect(point, connectRect);
            }
            return couldEventHide && !isInTriggerArea && !isInOverlayArea && !isInConnectRect;
        },
        [getConnectRect, getOverlayEle, getTriggerEle],
    );

    const fitPosition = useCallback(() => {
        const triggerEl = getTriggerEle();
        const overlayElFit = getOverlayEle();
        if (!triggerEl || !overlayElFit) return;

        const {
            offsetX = 0,
            offsetY = 0,
            menuPadding = { left: 0, right: 0, top: 0, bottom: 0 },
            isCanScrollPage = true,
            fitInViewAnyway = false,
            isAdaptive = true,
            offsetLeft = 0,
            offsetTop = 0,
            placement: placementProp,
        } = propsRef.current;

        const windowSize = { width: document.body.offsetWidth, height: document.body.offsetHeight };
        const visibleRect = {
            x: menuPadding.left,
            y: menuPadding.top,
            width: windowSize.width - menuPadding.left - menuPadding.right,
            height: windowSize.height - menuPadding.top - menuPadding.bottom,
        };
        let triggerRect = triggerEl.getBoundingClientRect();
        let overlayRect = overlayElFit.getBoundingClientRect();

        const __getTopWithMainLayout = (vertical: string) => {
            let t: number;
            switch (vertical.toLowerCase()) {
                case 'top':
                    t = triggerRect.top - overlayRect.height - offsetY;
                    break;
                case 'bottom':
                    t = triggerRect.top + triggerRect.height + offsetY;
                    break;
                default:
                    t = triggerRect.top + triggerRect.height;
                    break;
            }
            return t;
        };

        const __getLeftWithMainLayout = (horizontal: string) => {
            let l: number;
            switch (horizontal.toLowerCase()) {
                case 'left':
                    l = triggerRect.left - overlayRect.width - offsetX;
                    break;
                case 'right':
                    l = triggerRect.left + triggerRect.width + offsetX;
                    break;
                default:
                    l = triggerRect.left + triggerRect.width;
                    break;
            }
            return l;
        };

        const __getTopWithSubLayout = (vertical: string) => {
            let t: number;
            switch (vertical.toLowerCase()) {
                case 'top':
                    t = triggerRect.top;
                    break;
                case 'center':
                    t = triggerRect.top + triggerRect.height / 2 - overlayRect.height / 2;
                    break;
                case 'bottom':
                    t = triggerRect.top + triggerRect.height - overlayRect.height;
                    break;
                default:
                    t = triggerRect.top;
                    break;
            }
            return t;
        };

        const __getLeftWithSubLayout = (horizontal: string) => {
            let l: number;
            switch (horizontal.toLowerCase()) {
                case 'left':
                    l = triggerRect.left;
                    break;
                case 'center':
                    l = triggerRect.left + triggerRect.width / 2 - overlayRect.width / 2;
                    break;
                case 'right':
                    l = triggerRect.left + triggerRect.width - overlayRect.width;
                    break;
                default:
                    l = triggerRect.left;
                    break;
            }
            return l;
        };

        const __check = (position: { left: number; top: number }) =>
            position.left > visibleRect.x &&
            position.left + overlayRect.width < visibleRect.x + visibleRect.width &&
            position.top > visibleRect.y &&
            position.top + overlayRect.height < visibleRect.y + visibleRect.height;

        let isAdapt = false;
        type PositionResult = { left: number; top: number; newPlacement: string | undefined };
        const __getPosition = (placement: string, adaptive = false): PositionResult => {
            let l = 0;
            let t = 0;
            let newPlacement: string | undefined;
            switch (placement) {
                case 'bottomLeft':
                case 'bottomCenter':
                case 'bottomRight': {
                    let vertical = 'bottom';
                    let horizontal = placement.replace('bottom', '');
                    t = __getTopWithMainLayout(vertical);
                    l = __getLeftWithSubLayout(horizontal) + offsetX;
                    if (adaptive) {
                        if (t + overlayRect.height > visibleRect.y + visibleRect.height) vertical = 'top';
                        if (l < visibleRect.x) horizontal = 'Right';
                        else if (l + overlayRect.width > visibleRect.x + visibleRect.width) horizontal = 'Left';
                        newPlacement = vertical + horizontal;
                    }
                    break;
                }
                case 'topLeft':
                case 'topCenter':
                case 'topRight': {
                    let vertical = 'top';
                    let horizontal = placement.replace('top', '');
                    t = __getTopWithMainLayout(vertical);
                    l = __getLeftWithSubLayout(horizontal) + offsetX;
                    if (adaptive) {
                        if (t < visibleRect.y) vertical = 'bottom';
                        if (l < visibleRect.x) horizontal = 'Right';
                        else if (l + overlayRect.width > visibleRect.x + visibleRect.width) horizontal = 'Left';
                        newPlacement = vertical + horizontal;
                    }
                    break;
                }
                case 'rightTop':
                case 'rightCenter':
                case 'rightBottom': {
                    let horizontal = 'right';
                    let vertical = placement.replace('right', '');
                    l = __getLeftWithMainLayout(horizontal);
                    t = __getTopWithSubLayout(vertical) + offsetY;
                    if (adaptive) {
                        if (l + overlayRect.width > visibleRect.x + visibleRect.width) horizontal = 'left';
                        if (t < visibleRect.y) vertical = 'Top';
                        else if (t + overlayRect.height > visibleRect.y + visibleRect.height) vertical = 'Bottom';
                        newPlacement = horizontal + vertical;
                    }
                    break;
                }
                case 'leftTop':
                case 'leftCenter':
                case 'leftBottom': {
                    let horizontal = 'left';
                    let vertical = placement.replace('left', '');
                    l = __getLeftWithMainLayout(horizontal);
                    t = __getTopWithSubLayout(vertical) + offsetY;
                    if (adaptive) {
                        if (l < visibleRect.x) horizontal = 'right';
                        if (t < visibleRect.y) vertical = 'Top';
                        else if (t + overlayRect.height > visibleRect.y + visibleRect.height) vertical = 'Bottom';
                        newPlacement = horizontal + vertical;
                    }
                    break;
                }
                default:
                    l = triggerRect.left - overlayRect.width / 2 + triggerRect.width / 2;
                    t = triggerRect.top + triggerRect.height;
                    break;
            }

            if (adaptive && newPlacement !== placement && newPlacement) {
                const p = __getPosition(newPlacement);
                if (__check(p)) {
                    l = p.left;
                    t = p.top;
                    isAdapt = true;
                } else {
                    newPlacement = '';
                }
            }

            if (fitInViewAnyway) {
                const overlayElement = getOverlayEle();
                if (overlayElement) {
                    const currentOverlayRect = overlayElement.getBoundingClientRect();
                    const overlayHeight =
                        overlayElement.offsetHeight > 0 ? overlayElement.offsetHeight : currentOverlayRect.height;
                    const overlayWidth =
                        overlayElement.offsetWidth > 0 ? overlayElement.offsetWidth : currentOverlayRect.width;
                    const isTopOverflow = t < visibleRect.y;
                    const isBottomOverflow = t + overlayHeight > visibleRect.y + visibleRect.height;
                    const isRightOverflow = l + overlayWidth > visibleRect.x + visibleRect.width;

                    if (isTopOverflow || isBottomOverflow) {
                        if (overlayHeight <= visibleRect.height) {
                            t = visibleRect.y + visibleRect.height - overlayHeight;
                        } else {
                            t = visibleRect.y;
                        }
                        isAdapt = true;
                    }
                    if (isRightOverflow) {
                        if (overlayWidth <= visibleRect.width) {
                            l = triggerRect.left - overlayWidth - offsetX;
                            l = Math.max(l, visibleRect.x);
                        } else {
                            l = visibleRect.x;
                        }
                        isAdapt = true;
                    }
                }
            }

            if (isCanScrollPage && getOverlayEle()) {
                const parentEl = getOverlayEle()!.parentElement;
                if (parentEl) {
                    const parentRect = parentEl.getBoundingClientRect();
                    l -= parentRect.left;
                    t -= parentRect.top;
                }
            }

            return { left: l, top: t, newPlacement };
        };

        const placement = placementProp || 'bottomCenter';
        const { left: nextLeft, top: nextTop, newPlacement } = __getPosition(placement, isAdaptive);
        placementRef.current = newPlacement || placement;
        setLeft(isAdapt ? nextLeft : nextLeft + offsetLeft);
        setTop(isAdapt ? nextTop : nextTop + offsetTop);
    }, [getOverlayEle, getTriggerEle]);

    const fitPositionRef = useRef(fitPosition);
    fitPositionRef.current = fitPosition;

    const checkCanBeHideRef = useRef(checkCanBeHide);
    checkCanBeHideRef.current = checkCanBeHide;

    const hideRef = useRef<(e?: unknown) => void>(() => {});

    const _hide = useCallback((e?: unknown) => {
        if (!shownRef.current || propsRef.current.disabled) return;
        hideEventRef.current = e;
        setShown(false);
    }, []);

    const _show = useCallback((e?: unknown) => {
        void e;
        if (shownRef.current || propsRef.current.disabled) return;
        setShown(true);
    }, []);

    const hide = useCallback(
        (e?: unknown) => {
            if (!propsRef.current.ignoreManager)
                FJDropDownManager.hide({ id: idRef.current, level: levelRef.current, hideFunction: _hide });
            else _hide(e);
        },
        [_hide],
    );

    const show = useCallback(
        (e?: unknown) => {
            if (shownRef.current || propsRef.current.disabled) return;
            const p = propsRef.current;
            if (!p.ignoreManager) {
                FJDropDownManager.show({
                    id: idRef.current,
                    trigger: p.trigger ?? 'click',
                    level: levelRef.current,
                    checkCanBeHide,
                    hideFunction: _hide,
                    showFunction: _show,
                });
            } else {
                _show(e);
            }
        },
        [_show, _hide, checkCanBeHide],
    );

    hideRef.current = hide;

    const stableOnMouseDown = useCallback((e: globalThis.MouseEvent) => {
        e.stopPropagation();
        if (checkCanBeHideRef.current(e)) {
            hideRef.current(e);
        }
    }, []);

    const stableOnMousewheel = useCallback((e: globalThis.Event) => {
        const { isFF, isChrome } = _judgeBrowser();
        if (isFF) {
            document.removeEventListener('DOMMouseScroll', stableOnMousewheel);
        } else if (isChrome) {
            document.removeEventListener('scroll', stableOnMousewheel);
            document.removeEventListener('wheel', stableOnMousewheel);
        }
        hideRef.current(e);
    }, []);

    const stableOnResize = useCallback(() => {
        if (!shownRef.current) return;
        resizeDebounceRef.current ??= _.debounce(() => {
            fitPositionRef.current();
        }, 300);
        resizeDebounceRef.current();
    }, []);

    const unbindEvent = useCallback(() => {
        if (!isBindEventRef.current) return;
        const { trigger = 'click', isNeedBindWheelEvent = false, ignoreManager } = propsRef.current;
        if (ignoreManager)
            FJUtil.removeEvent(document.body, trigger === 'click' ? 'mousedown' : 'mousemove', stableOnMouseDown);
        if (isNeedBindWheelEvent && trigger === 'click') {
            const { isFF, isChrome } = _judgeBrowser();
            if (isFF) {
                document.removeEventListener('DOMMouseScroll', stableOnMousewheel);
            } else if (isChrome) {
                document.removeEventListener('scroll', stableOnMousewheel);
                document.removeEventListener('wheel', stableOnMousewheel);
            }
        }
        window.removeEventListener('resize', stableOnResize);
        isBindEventRef.current = false;
    }, [stableOnMouseDown, stableOnMousewheel, stableOnResize]);

    const bindEvent = useCallback(() => {
        if (isBindEventRef.current) return;
        const { trigger = 'click', isNeedBindWheelEvent = false, ignoreManager } = propsRef.current;
        if (ignoreManager)
            FJUtil.addEvent(document.body, trigger === 'click' ? 'mousedown' : 'mousemove', stableOnMouseDown);
        if (isNeedBindWheelEvent && trigger === 'click') {
            const { isFF, isChrome } = _judgeBrowser();
            if (isFF) {
                document.addEventListener('DOMMouseScroll', stableOnMousewheel);
            } else if (isChrome) {
                document.addEventListener('scroll', stableOnMousewheel);
                document.addEventListener('wheel', stableOnMousewheel);
            }
        }
        window.addEventListener('resize', stableOnResize);
        isBindEventRef.current = true;
    }, [stableOnMouseDown, stableOnMousewheel, stableOnResize]);

    const animationIn = useCallback(() => {
        if (hideTimerRef.current) {
            clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }

        const overlay = overlayEle.current;
        if (!overlay) return;

        overlay.style.display = 'block';
        overlay.style.visibility = 'hidden';

        fitPositionRef.current();
        setTimeout(() => {
            fitPositionRef.current();
            overlay.style.visibility = 'visible';
        }, 200);

        overlay.classList.remove(styles.out);

        if (!propsRef.current.noAnimation) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    overlay.classList.add(styles.in);
                });
            });
        } else {
            overlay.classList.add(styles.in);
        }
    }, []);

    const animationOut = useCallback(() => {
        const overlay = overlayEle.current;
        if (!overlay) return;

        if (!propsRef.current.noAnimation) {
            overlay.classList.remove(styles.in);
            overlay.classList.add(styles.out);
        } else {
            overlay.classList.remove(styles.in);
        }

        hideTimerRef.current = setTimeout(
            () => {
                overlay.style.display = 'none';
                overlay.style.visibility = 'hidden';
            },
            propsRef.current.noAnimation ? 0 : OVERLAY_CLOSE_MS,
        );
    }, []);

    useEffect(() => {
        if (!mountedTransitionRef.current) {
            mountedTransitionRef.current = true;
            return;
        }
        const p = propsRef.current;
        if (shown) {
            bindEvent();
            animationIn();
            p.onShow?.();
        } else {
            unbindEvent();
            animationOut();
            p.onHide?.(hideEventRef.current);
        }
    }, [shown, bindEvent, unbindEvent, animationIn, animationOut]);

    const _hideRef = useRef(_hide);
    _hideRef.current = _hide;

    useEffect(() => {
        return () => {
            unbindEvent();
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
            if (!propsRef.current.ignoreManager && idRef.current !== undefined) {
                FJDropDownManager.hide(
                    { id: idRef.current, level: levelRef.current, hideFunction: () => _hideRef.current() },
                    true,
                );
            }
        };
    }, [unbindEvent]);

    const onClick = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            const p = propsRef.current;
            if (p.disabled) return;
            if (p.triggerClick && !shownRef.current) {
                p.triggerClick(e);
            }
            shownRef.current ? hide(e) : show(e);
        },
        [hide, show],
    );

    const hover = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            if (propsRef.current.trigger !== 'hover' || shownRef.current) return;
            show(e);
        },
        [show],
    );

    const getArrowClassNames = () => {
        let arrowClassName: string | undefined;
        let containerClassName: string | undefined;
        if (props.hasArrow) {
            switch (placementRef.current) {
                case 'bottomLeft':
                case 'bottomCenter':
                case 'bottomRight':
                    arrowClassName = styles.bottom;
                    containerClassName = styles.panel_bottom;
                    break;
                case 'topLeft':
                case 'topCenter':
                case 'topRight':
                    arrowClassName = styles.top;
                    containerClassName = styles.panel_top;
                    break;
                case 'leftTop':
                case 'leftCenter':
                case 'leftBottom':
                    arrowClassName = styles.left;
                    containerClassName = styles.panel_left;
                    break;
                case 'rightTop':
                case 'rightCenter':
                case 'rightBottom':
                    arrowClassName = styles.right;
                    containerClassName = styles.panel_right;
                    break;
                default:
                    break;
            }
        }
        return { containerClassName, arrowClassName };
    };

    const { containerClassName, arrowClassName } = getArrowClassNames();
    const display = triggerDisplay || 'inline-block';

    return (
        <>
            {renderTrigger({
                ref: triggerEle,
                active: canTriggerActive ? shown : '',
                style: { display: active ? display : 'none' },
                onClick,
                onMouseMove: hover,
            })}
            <FJAnchor isTopLayer={isTopLayer} useBody={useAnchorBody} zIndex={zIndex}>
                <div
                    className={classNames(
                        styles.drop_down_box,
                        !props.noAnimation && styles.with_transition,
                        overlayClassName,
                        containerClassName,
                    )}
                    ref={overlayEle}
                    style={{
                        overflow: hasArrow ? 'visible' : 'hidden',
                        left,
                        top,
                        ...overlayStyle,
                    }}
                    data-auto-id='Editor_avatar'
                >
                    {hasArrow ? (
                        <div className={classNames(styles.arrow, arrowClass, arrowClassName)} style={arrowStyle} />
                    ) : null}
                    {renderOverlay({
                        ref: menuRef,
                        active: canTriggerActive ? shown : '',
                        hide,
                        show,
                    })}
                </div>
            </FJAnchor>
        </>
    );
}
